import { type PayloadAction, createSlice } from '@reduxjs/toolkit';

import type { Group } from '../domain/group';
import usePacket, { defaultPacketConfig, type Packet } from '../domain/packet';

export interface PendingGroupSlice {
    fixedSlots?: number;
    floatingSlots?: number;
    group: Group
}

// A fresh pending-group for the Add screen. It carries a default packet that
// only holds the feature/payment config so the "properties" block renders on
// Add (matching Edit). `isOverflowFixed` is intentionally left undefined so the
// slot preference still defaults to "no slots"; `reservedNumber` is coerced to 0
// on save. Built fresh each call to avoid sharing mutable references across resets.
const makeInitialState = (): PendingGroupSlice => ({
    group: {
        assignedFleets: [],
        defaultDuration: 0,
        groupConfigs: [],
        members: [],
        name: '',
        packets: [],
        prefix: '',
        relatesTo: [],
        packet: {
            config: {
                features: { ...defaultPacketConfig.features },
                payment: { ...defaultPacketConfig.payment },
            },
        } as unknown as Packet
    }
});

const initialState: PendingGroupSlice = makeInitialState();

const pendingGroupSlice = createSlice({
    initialState,
    name: 'temp-group',
    reducers: {
        clearPendingGroup: () => {
            return makeInitialState();
        }

        ,
        initializePendingGroup: () =>
            makeInitialState()
        ,
        setFixedSlots: (state, action: PayloadAction<number>) => {
            state.fixedSlots = action.payload;
            if (state.group.packet) {
                state.group.packet.reservedNumber = action.payload;
            }
            return state;
        },
        setFloatingSlots: (state, action: PayloadAction<number>) => {
            state.floatingSlots = action.payload;
            if (state.group.packet) {
                state.group.packet.overflowNumber = action.payload;
            }
            return state;
        },
        setName: (state, action: PayloadAction<string>) => {
            state.group.name = action.payload;
            return state;
        },
        setPendingGroup: (state, action: PayloadAction<Group>) => {
            const grp = {...action.payload};
            const packetFromPayload = {...action.payload.packet} as Packet;
            if (grp?.id) {
                const packet = grp.packet;
                if (typeof packet !== 'undefined') {
                    state.fixedSlots = usePacket.fixedSlots(packetFromPayload);
                    state.floatingSlots = usePacket.floatingSlots(packetFromPayload);
                    packetFromPayload.reservedNumber = usePacket.fixedSlots(packet) ?? 0;
                    packetFromPayload.overflowNumber = usePacket.floatingSlots(packet) ?? 0;
                }
                state.group = grp;
                state.group.packet = packetFromPayload;
            }
            return state;
        },
        updatePendingGroupProps: (state, action: PayloadAction<{ key: string, value: unknown }>) => {
            const { key, value } = action.payload;

            // Deep-set the (possibly multi-level, e.g. packet.config.payment.skipPayment)
            // path directly on the Immer draft, creating intermediate objects as needed.
            const keyParts = key.split('.');
            let target = state.group as Record<string, any>;
            for (let i = 0; i < keyParts.length - 1; i++) {
                const part = keyParts[i];
                if (target[part] == null || typeof target[part] !== 'object') {
                    target[part] = {};
                }
                target = target[part];
            }
            target[keyParts.at(-1)!] = value;
            return state;
        },
    },
});

export const { setPendingGroup, clearPendingGroup, updatePendingGroupProps, setFixedSlots, setFloatingSlots, setName } = pendingGroupSlice.actions;
export default pendingGroupSlice.reducer;